import React from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { RadioInputLabel, spacing } from '@cash-design-system/react'
import { FormBlockerElementEmojiPickerElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { unicodeToEmojiSvg } from '@cash-web/shared-feature-emojis'
import { RadioGroup, RadioGroupOption } from '@cash-web/shared-feature-form'

import { FormBlockerAdditionalElementProps } from '../FormBlockerElement/FormBlockerElement'

const EmojiRadioGroupContainer = styled.div`
  padding: 0 1rem;
`

const InputWrapper = styled.div`
  display: grid;
  column-gap: 3.75rem;
  grid-template-columns: repeat(3, 1fr);
  row-gap: ${spacing.l};
`

const EmojiRadioInputLabel = styled(RadioInputLabel)`
  border-radius: 50%;
  height: 5.5rem;
  padding: 0;
  width: 5.5rem;

  ${props =>
    props.checked &&
    css`
      background-color: ${props.theme.colors.formFieldBackground};
      border: ${props.theme.pxToRem(3)} solid ${props.theme.colors.primary};
    `}
`

const EmojiImage = styled.img`
  height: 3.5rem;
  margin: auto;
  width: 3.5rem;
`

const getEmoji = ({
  unicode,
  darkMode,
  useNewEmojis,
}: {
  unicode?: string
  darkMode: boolean
  useNewEmojis: boolean
}): string | undefined => {
  if (unicode == null) {
    throw Error(`EmojiPickerElement: Emoji "${unicode}" not found`)
  }

  return unicodeToEmojiSvg({ unicode, size: 48, darkMode, version: useNewEmojis ? 'new' : 'old' })
}

export function EmojiPickerElement({
  categories,
  initial_selection,

  // additional form blocker props
  formSectionIndex,
}: FormBlockerElementEmojiPickerElement & FormBlockerAdditionalElementProps): React.ReactElement {
  const { darkMode } = useTheme()
  const useNewEmojis = useFeatureFlag('cash-web-account-savings-use-updated-goal-images')

  const emoji = categories
    ?.flatMap(category =>
      category.emojiOptions?.map(emojiOption => {
        const emoji = emojiOption
          ? getEmoji({ unicode: emojiOption.unicodeEmoji, darkMode, useNewEmojis: !!useNewEmojis })
          : undefined
        return emoji
          ? {
              label: <EmojiImage alt={emojiOption.accessibilityText} src={emoji} />,
              value: emojiOption?.unicodeEmoji,
            }
          : null
      })
    )
    .filter(value => value != null) as RadioGroupOption[]

  const initialCategoryIndex = initial_selection?.categories_index || 0
  const initialOptionsIndex = initial_selection?.options_index || 0
  const defaultEmoji = categories?.[initialCategoryIndex]?.emojiOptions?.[initialOptionsIndex]?.unicodeEmoji

  return (
    <EmojiRadioGroupContainer>
      <RadioGroup
        defaultValue={defaultEmoji}
        InputWrapper={InputWrapper}
        name={`${formSectionIndex}.emoji_picker_result.selection`}
        options={emoji}
        StyledLabelComponent={EmojiRadioInputLabel}
      />
    </EmojiRadioGroupContainer>
  )
}
